import { publicUrl } from '../config'

async function fetchJson<T>(url: string): Promise<T> {
  const r = await fetch(url)
  if (!r.ok) throw new Error(`${r.status} ${r.statusText}`)
  return r.json() as Promise<T>
}

export type SiteEvent = {
  id: string
  slug: string
  title: string
  summary: string | null
  bodyHtml: string | null
  startsAt: string
  endsAt: string | null
  timezone: string
  venueName: string | null
  locationLabel: string | null
  addressLine: string | null
  ticketUrl: string | null
  bookinea_event_id: string | null
  lineupText: string | null
  displayStatus: 'upcoming' | 'past' | 'cancelled'
  /** CMS: featured CRT block on /events (only one per org). */
  promotedOnEventsPage?: boolean
  /** CMS: pinned as the "Rückblick" block on the home page (only one per org). */
  featuredOnHome?: boolean
  featuredImageUrl: string | null
  galleryUrls: string[] | null
  lineupArtistIds?: string[] | null
  guestArtistsJson?: SiteGuestArtist[] | null
}

export type SiteGuestArtist = {
  name: string
  websiteUrl?: string | null
  spotifyUrl?: string | null
  soundcloudUrl?: string | null
  bioHtml?: string | null
}

export type SiteLineupArtist = {
  id: string
  slug: string
  name: string
}

export type SiteEventFaqItem = {
  question: string
  answerHtml: string
}

export type SiteEventDetail = SiteEvent & {
  lineupArtists: SiteLineupArtist[]
  guestArtists: SiteGuestArtist[]
  faqJson?: SiteEventFaqItem[] | null
}

export type SiteArtist = {
  id: string
  slug: string
  name: string
  bioHtml: string | null
  websiteUrl: string | null
  socialLinksText: string | null
  portraitImageUrl: string | null
  spotifyUrl?: string | null
  soundcloudUrl?: string | null
  bandcampUrl?: string | null
  mixcloudUrl?: string | null
  tracksJson?: SiteTrack[] | null
  lineupEligible?: boolean
  relatedArtistsJson?: Array<{ slug: string; label?: string }> | null
}

export type SiteTrack = {
  title: string
  url?: string | null
  spotifyUrl?: string | null
  soundcloudUrl?: string | null
}

export type SiteRelatedArtist = {
  id: string
  slug: string
  name: string
  relationLabel: string | null
}

export type SiteArtistDetail = SiteArtist & {
  relatedArtists: SiteRelatedArtist[]
  releases: SiteRelease[]
}

export type SiteRelease = {
  id: string
  slug: string
  title: string
  releaseType: string
  releaseDate: string | null
  spotifyUrl: string | null
  soundcloudUrl: string | null
  mp3Url: string | null
  coverImageUrl: string | null
}

export type SitePage = {
  slug: string
  title: string
  heroImageUrl: string | null
  bodyHtml: string | null
}

export function fetchEvents(opts?: { status?: string }) {
  const q = new URLSearchParams()
  if (opts?.status) q.set('status', opts.status)
  const qs = q.toString()
  return fetchJson<{ events: SiteEvent[] }>(
    publicUrl(`/events${qs ? `?${qs}` : ''}`)
  )
}

export function fetchEvent(slug: string) {
  return fetchJson<SiteEventDetail>(publicUrl(`/events/${encodeURIComponent(slug)}`))
}

export function fetchArtists() {
  return fetchJson<{ artists: SiteArtist[] }>(publicUrl('/artists'))
}

export function fetchArtist(slug: string) {
  return fetchJson<SiteArtistDetail>(publicUrl(`/artists/${encodeURIComponent(slug)}`))
}

export function fetchReleases() {
  return fetchJson<{ releases: SiteRelease[] }>(publicUrl('/releases'))
}

export function fetchPage(slug: string) {
  return fetchJson<SitePage>(publicUrl(`/pages/${encodeURIComponent(slug)}`))
}

export type SiteTeamMember = {
  id: string
  slug: string
  displayName: string
  photoUrl: string | null
  linkedArtistId: string | null
  sortOrder: number
}

export function fetchTeam() {
  return fetchJson<{ team: SiteTeamMember[] }>(publicUrl('/team'))
}

export type SiteFaqItem = {
  id: string
  question: string
  answerHtml: string
  sortOrder: number
}

export function fetchFaq() {
  return fetchJson<{ faq: SiteFaqItem[] }>(publicUrl('/faq'))
}

export type SiteShopItem = {
  id: string
  title: string
  subtitle: string | null
  imageUrl: string | null
  linkUrl: string | null
  sortOrder: number
}

export function fetchShopItems() {
  return fetchJson<{ items: SiteShopItem[] }>(publicUrl('/shop'))
}

export async function postContact(payload: {
  name: string
  email: string
  subject: string
  message: string
}): Promise<{ ok: boolean }> {
  const r = await fetch(publicUrl('/contact'), {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(payload),
  })
  if (!r.ok) {
    const err = await r.json().catch(() => ({}))
    throw new Error((err as { error?: string }).error || r.statusText)
  }
  return r.json() as Promise<{ ok: boolean }>
}

export type SiteLinkItem = {
  id: string
  label: string
  url: string
  emoji: string | null
  sortOrder: number
  activeFrom: string | null
  activeTo: string | null
  published: boolean
}

export function fetchLinks() {
  return fetchJson<{ links: SiteLinkItem[] }>(publicUrl('/links'))
}

export function fetchBranding() {
  return fetchJson<{ logoUrl: string | null; faviconUrl: string | null }>(publicUrl('/branding'))
}
